// The app bar's pure rules (D-161): preferences, the badge rule, Home's address. No React, so they are unit-tested.
import { DUGZI_APPS } from './apps-registry';

export type BadgeMode = 'count' | 'dot' | 'none';
export interface AppBarPrefs {
  v: 1;
  /** Pinned app ids, in order (up to 9; phones show the first 4). */
  pins: string[];
  badges: Record<string, BadgeMode>;
  /** "Show badges on the bar" off: for screens others can see. The bell keeps everything. */
  quiet: boolean;
  /** When it was last changed, so the newest copy wins when the cookie and Account differ. */
  t: number;
}
export const DEFAULT_PINS = ['mail', 'calendar', 'chat', 'drive', 'docs', 'sheets', 'tasks'];
/** What counts by default (the badge rule): things someone waits for get a number, shared work a dot. */
export const DEFAULT_BADGES: Record<string, BadgeMode> = { calendar: 'count', chat: 'count', tasks: 'count', mail: 'dot', docs: 'dot', sheets: 'dot', slides: 'dot', drive: 'dot' };
export const MAX_PINS = 9;
export const COOKIE = 'dz_bar';

export const defaultPrefs = (): AppBarPrefs => ({ v: 1, pins: [...DEFAULT_PINS], badges: {}, quiet: false, t: 0 });
export const badgeModeOf = (p: AppBarPrefs, id: string): BadgeMode => p.badges[id] ?? DEFAULT_BADGES[id] ?? 'none';

/** Validates anything read from a cookie or from Account: unknown ids dropped, sizes capped. */
export function cleanPrefs(raw: unknown, known: readonly string[] = DUGZI_APPS.map((a) => a.id)): AppBarPrefs {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Partial<AppBarPrefs>;
  const pins = Array.isArray(o.pins) ? [...new Set(o.pins.filter((x): x is string => typeof x === 'string' && known.includes(x)))].slice(0, MAX_PINS) : null;
  const badges: Record<string, BadgeMode> = {};
  if (o.badges && typeof o.badges === 'object') for (const [k, v] of Object.entries(o.badges)) if (known.includes(k) && (v === 'count' || v === 'dot' || v === 'none')) badges[k] = v;
  return { v: 1, pins: pins ?? [...DEFAULT_PINS], badges, quiet: o.quiet === true, t: typeof o.t === 'number' ? o.t : 0 };
}

/** dugzi.com, the signed-in Home: Dugzi Account's address without `accounts.`. */
export function homeUrl(accountsUrl: string): string {
  const u = new URL(accountsUrl);
  u.hostname = u.hostname.replace(/^accounts\./, '');
  return u.origin + '/';
}

export function badgeLabel(name: string, n: number, mode: BadgeMode, quiet: boolean, online: boolean): string {
  if (quiet || !online || mode === 'none' || n <= 0) return name;
  return mode === 'count' ? `${name}, ${n} ${n === 1 ? 'thing is' : 'things are'} waiting for you` : `${name}, something new for you`;
}


/** Account's copy of the app bar is account-bound: every read and write names the displayed account
 * (`X-Dugzi-Account`), and without one there is no request at all. */
export function appBarRequest(account: string | undefined, prefs?: AppBarPrefs): RequestInit | null {
  if (!account) return null;
  const headers: Record<string, string> = { 'X-Dugzi-Account': account };
  if (prefs === undefined) return { headers, credentials: 'same-origin' };
  headers['Content-Type'] = 'application/json';
  return { method: 'PUT', headers, credentials: 'same-origin', body: JSON.stringify(prefs) };
}
